import { getHomeboxServiceUrl } from "../utils/urlConfig";
import React, { createContext, useState, useEffect } from 'react';

// Create a context to manage authentication-related state and actions
export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  // Initialize user state to null (no user is logged in initially)
  const [user, setUser] = useState(null);
  const [profileImage, setProfileImage] = useState(null);

  // Track the loading state (whether the app is still checking for user info)
  const [isLoading, setIsLoading] = useState(true);

  const fetchProfileImage = async (token) => {
    try {
      const MAIN_API_URL = getHomeboxServiceUrl();
      const response = await fetch(`${MAIN_API_URL}/api/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProfileImage(data.profileImagePath);
      }
    } catch (error) {
      console.error("Failed to fetch profile image in context", error);
    }
  };

  /**
   * Logs in the user by updating the user state and storing user data in localStorage.
   * 
   * @param {Object} data - The user data to store (typically from login response).
   */
  const loginUser = (data) => {
    setUser(data); // Set the user data in state
    localStorage.setItem('user', JSON.stringify(data)); // Store the user data in localStorage
    const token = data.auth_token || data.token || data.jwt;
    if (token) fetchProfileImage(token);
  };

  /**
   * Logs out the user by clearing the user state and removing user data from localStorage.
   */
  const logoutUser = () => {
    setUser(null); // Clear user data from state
    setProfileImage(null);
    localStorage.removeItem('user'); // Remove the user data from localStorage
  };

  // useEffect hook to check if there's a stored user in localStorage when the app loads
  useEffect(() => {
    const storedUser = localStorage.getItem('user'); // Retrieve the stored user data from localStorage
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      setUser(parsedUser); // Parse and set the user if found in localStorage
      const token = parsedUser.auth_token || parsedUser.token || parsedUser.jwt;
      if (token) fetchProfileImage(token);
    }
    setIsLoading(false); // Once user check is complete, stop loading
  }, []); // The empty dependency array ensures this effect runs only once when the component mounts

  return (
    // Provide the AuthContext to child components
    <AuthContext.Provider value={{ user, isLoading, loginUser, logoutUser, profileImage, setProfileImage }}>
      {children}  {/* Render the child components */}
    </AuthContext.Provider>
  );
};
